import { useState, useMemo, useRef } from 'react'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import {
  resolveFilterVisitMode,
  useFilterVisitGuard,
  type FilterVisitMode,
} from '@/shared/hooks/useFilterVisitGuard'
import { useHydrateLocationFilters } from '@/shared/hooks/useHydrateLocationFilters'
import { Link, router, usePage } from '@inertiajs/react'
import {
  clearLocationFilters,
  parseLocationIdCsv,
  writeLocationFilters,
} from '@/shared/lib/locationFiltersSession'
import type { SharedProps } from '@/shared/types/SharedProps'
import { ImageOff, SlidersHorizontal, X } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { MultiSelectCombobox } from '@/shared/ui/multi-select-combobox'
import type { MultiSelectOption } from '@/shared/ui/multi-select-combobox'
import { Badge } from '@/shared/ui/badge'
import { TaskStatusBadge } from '@/features/task-status'
import { instrumentStatusBadgeVariant } from '@/shared/lib/instrumentStatusBadge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/shared/ui/sheet'
import DataPagination from '@/shared/ui/DataPagination'
import { t } from '@/shared/lib/i18n'
import { clearOrgFiltersParams } from '@/shared/lib/persistedOrgFilters'
import {
  buildOrgCascadeChangeHandlers,
  visibleDepartmentOptionsForSites,
  visibleUnitOptionsForParents,
  type OrgCascadeSelection,
} from '@/shared/lib/orgMultiSelectCascade'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { instrumentPath, instrumentsPath, newInstrumentPath } from '@/shared/lib/routes'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { useStatusPoll } from '@/shared/hooks/useStatusPoll'
import { STATUS_POLL_INTERVAL_MS } from '@/shared/constants/statusPolling'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type { WebInstrumentDashboard } from '@/shared/types/web'

interface SelectOption {
  value: number
  label: string
}

interface DepartmentOption {
  value: number
  label: string
  siteId: number | null
}

interface UnitOption {
  value: number
  label: string
  siteId: number | null
  departmentId: number | null
}

interface TaskStatusOption {
  id: number
  code: string
  description: string
}

interface Filters {
  searchText: string
  siteIds: string
  departmentIds: string
  unitIds: string
  tagIds: string
  codeValueIds: string
  showDisabled: boolean
}

interface NavigateOverrides {
  siteIds?: number[]
  deptIds?: number[]
  unitIds?: number[]
  searchText?: string
  tagIds?: string
  codeValueIds?: string
  showDisabled?: boolean
  page?: number
  // REBUILD-553: debounced search sets preserveState so the input keeps focus.
  // Selection commits use FilterVisitMode 'commit' and keep the dropdown mounted (REBUILD-123).
  preserveState?: boolean
}

interface Props {
  instruments: WebInstrumentDashboard[]
  pagination: PaginationMeta
  filters: Filters
  siteOptions: SelectOption[]
  departmentOptions: DepartmentOption[]
  unitOptions: UnitOption[]
  tagOptions: MultiSelectOption[]
  taskStatusOptions: TaskStatusOption[]
  [key: string]: unknown
}

// REBUILD-554: the failure pill keeps the rolling-window count but shows a resolved state once
// every failure in the window has been corrected by a later passing log (open === 0).
const FailureCountBadge = ({ total, open }: { total: number; open: number }) => {
  const resolved = open === 0
  return (
    <Badge variant={resolved ? 'secondary' : 'destructive'} className="text-xs">
      {total} {resolved ? t('instruments.failuresResolved') : t('instruments.failures')}
    </Badge>
  )
}

const InstrumentDashboardPage = () => {
  const {
    instruments,
    pagination,
    filters,
    siteOptions,
    departmentOptions,
    unitOptions,
    tagOptions,
    taskStatusOptions,
  } = usePage<Props>().props
  const { currentTenantId, currentUserInfo } = usePage<SharedProps>().props
  const currentUserId = currentUserInfo?.id ?? null
  const canCreate = useCan(Resources.INSTRUMENT_MAINTENANCE, Actions.CREATE)
  useStatusPoll(STATUS_POLL_INTERVAL_MS, { only: ['instruments'], onNetworkError: () => false })
  useBreadcrumbs([{ label: t('nav.instruments') }])

  const sheetOpenRef = useRef(false)

  const [searchText, setSearchText] = useState(filters.searchText)
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>(
    filters.siteIds ? filters.siteIds.split(',').map(Number).filter(Boolean) : [],
  )
  const [selectedDeptIds, setSelectedDeptIds] = useState<number[]>(
    filters.departmentIds ? filters.departmentIds.split(',').map(Number).filter(Boolean) : [],
  )
  const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>(
    filters.unitIds ? filters.unitIds.split(',').map(Number).filter(Boolean) : [],
  )
  const [tagIds, setTagIds] = useState<number[]>(
    filters.tagIds ? filters.tagIds.split(',').map(Number).filter(Boolean) : [],
  )
  const [selectedTaskStatusIds, setSelectedTaskStatusIds] = useState<number[]>(
    filters.codeValueIds ? filters.codeValueIds.split(',').map(Number).filter(Boolean) : [],
  )
  const [showDisabled, setShowDisabled] = useState(filters.showDisabled ?? false)

  const { beginVisit } = useFilterVisitGuard()

  const navigate = (overrides: NavigateOverrides = {}, mode: FilterVisitMode = 'default') => {
    const isPageChange = overrides.page != null
    const sites = overrides.siteIds ?? selectedSiteIds
    const depts = overrides.deptIds ?? selectedDeptIds
    const units = overrides.unitIds ?? selectedUnitIds
    const q = (overrides.searchText ?? searchText).trim()
    const tg = overrides.tagIds ?? tagIds.join(',')
    const cv = overrides.codeValueIds ?? selectedTaskStatusIds.join(',')
    const includeDisabled = overrides.showDisabled ?? showDisabled

    const filterParams: Record<string, string> = {}
    if (q) filterParams.search_text = q
    // Always send org filter keys (empty when none) so the server can write session
    // persistence and distinguish apply/deselect from bare navigation.
    filterParams.site_ids = sites.join(',')
    filterParams.department_ids = depts.join(',')
    filterParams.unit_ids = units.join(',')
    if (tg) filterParams.tag_ids = tg
    if (cv) filterParams.code_value_ids = cv
    if (includeDisabled) filterParams.show_disabled = '1'
    if (overrides.page != null) filterParams.page = String(overrides.page)

    writeLocationFilters(currentTenantId, currentUserId, {
      siteIds: sites,
      departmentIds: depts,
      unitIds: units,
    })

    // REBUILD-553: preserve component state (and scroll) for the live search so Inertia reuses
    // the mounted page instead of remounting it — the search input keeps focus mid-keystroke
    // across the debounced results refresh. Only the `instruments` prop needs to refresh for a
    // search, so scope the partial reload to it to avoid re-rendering unrelated filter widgets.
    const preserveState = overrides.preserveState ?? false
    const href = instrumentsPath(filterParams)
    const visit = beginVisit(href, resolveFilterVisitMode(sheetOpenRef.current, mode))
    if (!visit) return
    const keepDropdownOpen = visit.keepDropdownOpen
    router.visit(href, {
      preserveState: keepDropdownOpen || preserveState,
      ...((keepDropdownOpen || preserveState) && !isPageChange ? { preserveScroll: true } : {}),
      ...(preserveState && !keepDropdownOpen && !isPageChange
        ? { only: ['instruments', 'pagination'] }
        : {}),
    })
  }

  useHydrateLocationFilters({
    tenantId: currentTenantId,
    userId: currentUserId,
    hasUrlLocationParams:
      parseLocationIdCsv(filters.siteIds).length > 0 ||
      parseLocationIdCsv(filters.departmentIds).length > 0 ||
      parseLocationIdCsv(filters.unitIds).length > 0,
    currentFilters: {
      siteIds: parseLocationIdCsv(filters.siteIds),
      departmentIds: parseLocationIdCsv(filters.departmentIds),
      unitIds: parseLocationIdCsv(filters.unitIds),
    },
    siteOptions,
    departmentOptions,
    unitOptions,
    onHydrate: (hydrated) => {
      setSelectedSiteIds(hydrated.siteIds)
      setSelectedDeptIds(hydrated.departmentIds)
      setSelectedUnitIds(hydrated.unitIds)
      navigate({
        siteIds: hydrated.siteIds,
        deptIds: hydrated.departmentIds,
        unitIds: hydrated.unitIds,
      })
    },
  })

  const debouncedNavigate = useDebouncedCallback((overrides: NavigateOverrides) => {
    navigate(overrides)
  }, 400)

  const handleSearchChange = (value: string) => {
    if (value.trim() === '') {
      setSearchText('')
      debouncedNavigate.cancel()
      // REBUILD-553: preserveState avoids remounting the page so the search input keeps focus.
      navigate({ searchText: '', preserveState: true })
      return
    }
    setSearchText(value)
    // REBUILD-553: preserveState keeps the input mounted/focused across the debounced refresh.
    debouncedNavigate({ searchText: value, preserveState: true })
  }

  const orgCascade = buildOrgCascadeChangeHandlers({
    departmentOptions,
    unitOptions,
    selection: {
      siteIds: selectedSiteIds,
      departmentIds: selectedDeptIds,
      unitIds: selectedUnitIds,
    },
    applySelection: ({ siteIds, departmentIds, unitIds }) => {
      setSelectedSiteIds(siteIds)
      setSelectedDeptIds(departmentIds)
      setSelectedUnitIds(unitIds)
    },
  })

  const navigateWithOrgSelection = (
    { siteIds, departmentIds, unitIds }: OrgCascadeSelection,
    mode: FilterVisitMode = 'default',
  ) => navigate({ siteIds, deptIds: departmentIds, unitIds }, mode)

  const closeSheetWithCascadePrune = () => {
    debouncedNavigate.cancel()
    navigateWithOrgSelection(orgCascade.pruneSelection(), 'sheet-close')
  }

  const filteredDepartmentOptions = useMemo(
    () => visibleDepartmentOptionsForSites(departmentOptions, selectedSiteIds),
    [departmentOptions, selectedSiteIds],
  )

  const filteredUnitOptions = useMemo(
    () => visibleUnitOptionsForParents(unitOptions, selectedSiteIds, selectedDeptIds),
    [unitOptions, selectedSiteIds, selectedDeptIds],
  )

  const handleSiteCommit = (newSiteIds: number[]) =>
    navigateWithOrgSelection(orgCascade.changeSites(newSiteIds), 'commit')

  const handleDeptCommit = (newDeptIds: number[]) =>
    navigateWithOrgSelection(orgCascade.changeDepartments(newDeptIds), 'commit')

  const clearFilters = () => {
    debouncedNavigate.cancel()
    setSearchText('')
    setSelectedSiteIds([])
    setSelectedDeptIds([])
    setSelectedUnitIds([])
    setTagIds([])
    setSelectedTaskStatusIds([])
    setShowDisabled(false)
    clearLocationFilters(currentTenantId, currentUserId)
    router.visit(instrumentsPath(clearOrgFiltersParams()), { preserveState: false })
  }

  const goToPage = (page: number) => {
    debouncedNavigate.cancel()
    navigate({ page, preserveState: true })
  }

  const activeFilterCount = [
    searchText,
    selectedSiteIds.length > 0,
    selectedDeptIds.length > 0,
    selectedUnitIds.length > 0,
    tagIds.length > 0,
    selectedTaskStatusIds.length > 0,
    showDisabled,
  ].filter(Boolean).length

  const renderInstrumentTags = (instrument: WebInstrumentDashboard) => {
    if (instrument.instrumentTags.length === 0) return null

    return (
      <p className="text-xs text-muted-foreground">
        {t('instruments.tagsLabel')}{' '}
        <span className="font-medium text-[#5086d9]">
          {instrument.instrumentTags.map((tag) => tag.name).join(', ')}
        </span>
      </p>
    )
  }

  const renderFilterInputs = () => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end">
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('instruments.filterSearch')}</Label>
        <Input
          placeholder={t('instruments.searchPlaceholder')}
          value={searchText}
          onChange={(event) => handleSearchChange(event.target.value)}
          className="w-full lg:w-60"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('instruments.filterSite')}</Label>
        <MultiSelectCombobox
          options={siteOptions}
          selected={selectedSiteIds}
          onChange={orgCascade.changeSites}
          onClose={handleSiteCommit}
          placeholder={t('instruments.allSites')}
          className="w-full lg:w-48"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('instruments.filterDepartment')}</Label>
        <MultiSelectCombobox
          options={filteredDepartmentOptions}
          selected={selectedDeptIds}
          onChange={orgCascade.changeDepartments}
          onClose={handleDeptCommit}
          placeholder={t('instruments.allDepartments')}
          className="w-full lg:w-44"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('instruments.filterUnit')}</Label>
        <MultiSelectCombobox
          options={filteredUnitOptions}
          selected={selectedUnitIds}
          onChange={setSelectedUnitIds}
          onClose={(selected) => navigate({ unitIds: selected }, 'commit')}
          placeholder={t('instruments.allUnits')}
          className="w-full lg:w-36"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('instruments.filterTags')}</Label>
        <MultiSelectCombobox
          options={tagOptions}
          selected={tagIds}
          onChange={setTagIds}
          onClose={(ids) => navigate({ tagIds: ids.join(',') }, 'commit')}
          placeholder={t('instruments.tagsPlaceholder')}
          searchPlaceholder={t('instruments.filterTags')}
          className="w-full lg:w-48"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('instruments.filterTaskStatus')}</Label>
        <MultiSelectCombobox
          options={taskStatusOptions.map((option) => ({
            value: option.id,
            label: option.description,
          }))}
          selected={selectedTaskStatusIds}
          onChange={setSelectedTaskStatusIds}
          onClose={(ids) => navigate({ codeValueIds: ids.join(',') }, 'commit')}
          placeholder={t('instruments.taskStatusPlaceholder')}
          className="w-full lg:w-44"
          showBadges={false}
        />
      </div>

      <Label className="flex items-center gap-2 font-normal lg:mb-2">
        <Switch
          checked={showDisabled}
          onCheckedChange={(checked) => {
            setShowDisabled(checked)
            navigate({ showDisabled: checked })
          }}
        />
        {t('instruments.showDisabled')}
      </Label>
    </div>
  )

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold sm:text-2xl">{t('instruments.title')}</h1>
        <div className="flex items-center gap-2">
          {/* Mobile filter button */}
          <div className="lg:hidden">
            <Sheet
              onOpenChange={(open) => {
                sheetOpenRef.current = open
                if (open) {
                  debouncedNavigate.cancel()
                } else {
                  // Prune on close — MultiSelect onClose may not run (e.g. Escape).
                  closeSheetWithCascadePrune()
                }
              }}
            >
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  {t('instruments.filtersTitle')}
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1 text-xs">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="rounded-t-xl px-4 pt-4 pb-8"
                title={t('instruments.filtersTitle')}
                hideCloseButton
              >
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-semibold">{t('instruments.filtersTitle')}</h2>
                  <div className="flex items-center gap-2">
                    {activeFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        onClick={clearFilters}
                      >
                        {t('instruments.filtersClear')}
                      </Button>
                    )}
                    <SheetClose asChild>
                      <Button variant="ghost" size="icon" aria-label={t('instruments.filtersDone')}>
                        <X className="h-4 w-4" />
                      </Button>
                    </SheetClose>
                  </div>
                </div>
                <div className="space-y-3">{renderFilterInputs()}</div>
              </SheetContent>
            </Sheet>
          </div>
          {canCreate && (
            <Button asChild>
              <Link href={newInstrumentPath()}>{t('instruments.newButton')}</Link>
            </Button>
          )}
        </div>
      </div>

      {/* Desktop inline filters */}
      <div className="hidden rounded-md border p-4 lg:block">
        <div className="flex flex-col gap-3 no-wrap">
          {renderFilterInputs()}
          {activeFilterCount > 0 && (
            <div className="flex items-center lg:justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={clearFilters}
              >
                {t('instruments.filtersClear')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile: card list */}
      <div className="space-y-2 lg:hidden">
        {instruments.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <ImageOff className="h-5 w-5 text-muted-foreground" />
            </span>
            <p className="text-sm text-muted-foreground">{t('instruments.empty')}</p>
          </div>
        ) : (
          instruments.map((instrument) => {
            const isInactive = instrument.active === 0
            return (
              <button
                key={instrument.id}
                type="button"
                className={`flex w-full cursor-pointer items-start gap-3 rounded-lg border bg-card p-3 text-left transition-colors hover:bg-muted/40 active:scale-[0.995] ${isInactive ? 'opacity-60' : ''}`}
                onClick={() => router.visit(instrumentPath(instrument.id))}
              >
                {instrument.imgUrl ? (
                  <img
                    src={instrument.imgUrl}
                    alt=""
                    className="mt-0.5 h-10 w-10 shrink-0 rounded object-cover"
                  />
                ) : (
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded bg-muted">
                    <ImageOff className="h-4 w-4 text-muted-foreground" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="truncate font-medium text-primary">
                      {instrument.instrumentLabel}
                    </p>
                  </div>
                  {renderInstrumentTags(instrument)}
                  {instrument.serialNumber && (
                    <p className="text-xs text-muted-foreground">S/N: {instrument.serialNumber}</p>
                  )}
                  <p className="mt-0.5 truncate text-sm text-muted-foreground">
                    {[instrument.departmentName, instrument.unitName, instrument.siteName]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {instrument.statusLabel && (
                      <Badge
                        variant={instrumentStatusBadgeVariant(instrument.statusCode)}
                        className="text-xs"
                      >
                        {instrument.statusLabel}
                      </Badge>
                    )}
                    <TaskStatusBadge
                      taskStatusCode={instrument.taskStatusCode}
                      className="text-xs"
                    />
                    {(instrument.rollingFailureCount ?? 0) > 0 && (
                      <FailureCountBadge
                        total={instrument.rollingFailureCount}
                        open={instrument.openFailureCount}
                      />
                    )}
                    {(instrument.rollingServiceReportCount ?? 0) > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {instrument.rollingServiceReportCount} {t('instruments.serviceReports')}
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            )
          })
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden rounded-md border lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('instruments.table.label')}</TableHead>
              <TableHead>{t('instruments.table.serial')}</TableHead>
              <TableHead>{t('instruments.table.department')}</TableHead>
              <TableHead>{t('instruments.table.site')}</TableHead>
              <TableHead>{t('instruments.table.status')}</TableHead>
              <TableHead>{t('instruments.table.taskStatus')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {instruments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                      <ImageOff className="h-5 w-5 text-muted-foreground" />
                    </span>
                    <p className="text-sm text-muted-foreground">{t('instruments.empty')}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              instruments.map((instrument) => {
                const showUrl = instrumentPath(instrument.id)
                const isInactive = instrument.active === 0
                return (
                  <TableRow
                    key={instrument.id}
                    className={`cursor-pointer ${isInactive ? 'opacity-60' : ''}`}
                    onClick={() => router.visit(showUrl)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        router.visit(showUrl)
                      }
                    }}
                    tabIndex={0}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        {instrument.imgUrl ? (
                          <img
                            src={instrument.imgUrl}
                            alt=""
                            className="h-8 w-8 shrink-0 rounded object-cover"
                          />
                        ) : (
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-muted">
                            <ImageOff className="h-4 w-4 text-muted-foreground" />
                          </span>
                        )}
                        <div className="space-y-0.5">
                          <Link
                            href={instrumentPath(instrument.id)}
                            className="text-primary underline-offset-4 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {instrument.instrumentLabel}
                          </Link>
                          {renderInstrumentTags(instrument)}
                          {((instrument.rollingFailureCount ?? 0) > 0 ||
                            (instrument.rollingServiceReportCount ?? 0) > 0) && (
                            <div className="flex flex-wrap items-center gap-1.5">
                              {(instrument.rollingFailureCount ?? 0) > 0 && (
                                <FailureCountBadge
                                  total={instrument.rollingFailureCount}
                                  open={instrument.openFailureCount}
                                />
                              )}
                              {(instrument.rollingServiceReportCount ?? 0) > 0 && (
                                <Badge variant="secondary" className="text-xs">
                                  {instrument.rollingServiceReportCount}{' '}
                                  {t('instruments.serviceReports')}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {instrument.serialNumber ?? '—'}
                    </TableCell>
                    <TableCell>
                      {instrument.departmentName ? (
                        <span>
                          {instrument.departmentName}
                          {instrument.unitName && (
                            <span className="text-muted-foreground"> / {instrument.unitName}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {instrument.siteName ?? '—'}
                    </TableCell>
                    <TableCell>
                      {instrument.statusLabel ? (
                        <Badge variant={instrumentStatusBadgeVariant(instrument.statusCode)}>
                          {instrument.statusLabel}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <TaskStatusBadge taskStatusCode={instrument.taskStatusCode} />
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      <DataPagination
        meta={pagination}
        onPageChange={goToPage}
        noun={t('instruments.paginationNoun')}
      />
    </div>
  )
}

export default InstrumentDashboardPage
